import { useRouterState } from "@tanstack/react-router";
import {
	LOCALE_LABELS,
	localeFromPath,
	localizedPathFor,
	stripLocalePrefix,
} from "#/lib/locale";
import { cn } from "#/lib/utils";

/**
 * Always-in-DOM language links for crawlers — the popover switcher only exists
 * in the DOM while open, so without these the SSR HTML contains no
 * <a> between the en and /ko trees (hreflang alone gets low crawl priority).
 * Rendered only on pages that have locale mirrors; each item links to the
 * current page in the other locale — the same targets as the hreflang
 * alternates. The current locale (from the URL, not the atom) is inert text.
 */
export function LocaleLinks({ className }: { className?: string }) {
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const logicalPath = stripLocalePrefix(pathname);
	const currentLocale = localeFromPath(pathname) ?? "en";

	// Only en + ko have URL trees; a page without a ko mirror has nothing to link.
	const koHref = localizedPathFor(logicalPath, "ko");
	if (!koHref) return null;
	const items = [
		{ locale: "en", href: logicalPath },
		{ locale: "ko", href: koHref },
	] as const;

	return (
		<nav
			aria-label="Language"
			className={cn("flex items-center gap-3", className)}
		>
			{items.map(({ locale, href }) =>
				locale === currentLocale ? (
					<span key={locale} className="text-foreground">
						{LOCALE_LABELS[locale]}
					</span>
				) : (
					<a
						key={locale}
						href={href}
						className="text-muted-foreground no-underline transition-colors hover:text-foreground"
					>
						{LOCALE_LABELS[locale]}
					</a>
				),
			)}
		</nav>
	);
}
